import { useState, useEffect } from "react";
import moment from "moment";

import { Button, Divider, Form, Input, Modal, Select, Typography} from "antd";
import TextArea from "antd/lib/input/TextArea";

import notifier from "utils/notifier";
import { formatDate, getEnumKeyByEnumValue } from "utils/helpers";
import { ErrorResponse } from "utils/types";
import { CMWorkListAcuity, CMWorkListStatus } from "utils/enums";

import { SaveTask } from "store/eligibility/mcp/actions";

import FloatLabel from "components/Common/FloatLabel";
import DatePickerLocalised from "components/Common/DatePickerLocalised";

interface CaseManagementWorkListModalProps {
  eligibilityMemberInfo: any;
  primaryCareProvider: any;
  isCmWorkListModalVisible: boolean;
  isCmWorkListModalVisibleHandler(): any;
  saveTask(task: SaveTask): any;
  updateOrNewTask: string;
  cmWorklistUpdateModal: any;
}

const { Title } = Typography;

const CaseManagementWorkListModal = (props: CaseManagementWorkListModalProps) => {
  const [form] = Form.useForm();
  const [taskOwner, setTaskOwner] = useState("");
  const [taskAcuityId, setTaskAcuityId] = useState(1);
  const [taskDescription, setTaskDescription] = useState("");
  const [dueDate, setDueDate] = useState(moment().format('MM/DD/YYYY'));
  const [completedDate, setCompletedDate] = useState("");
  const [nextReviewDate, setNextReviewDate] = useState(moment().add(3, 'days').format('MM/DD/YYYY'));
  const [membId, setMembId] = useState("");
  const [taskStatusId, setTaskStatusId] = useState(CMWorkListStatus.OPEN);
  const [uId, setUid] = useState(0);

  useEffect(() => {
    // reusing the same modal window for add new and update tasks
    //if the props.updateOrNewTask is updateTask, then we are using the modal to update.
    if (props.updateOrNewTask === "updateTask") {
      setUid(props.cmWorklistUpdateModal.RmiUid);
      setTaskOwner(props.cmWorklistUpdateModal.RmiTaskOwner);
      setTaskAcuityId(props.cmWorklistUpdateModal.RmiTaskAcuityId);
      setTaskStatusId(props.cmWorklistUpdateModal.RmiTaskStatusId);
      setTaskDescription(props.cmWorklistUpdateModal.RmiTaskDescription);
      setDueDate(formatDate(props.cmWorklistUpdateModal.RmiDueDate, 'MM/DD/YYYY'));
      setCompletedDate(formatDate(props.cmWorklistUpdateModal.RmiCompletedDate, 'MM/DD/YYYY'));
      setNextReviewDate(formatDate(props.cmWorklistUpdateModal.RmiNextReviewDate, 'MM/DD/YYYY'));
      setMembId(props.cmWorklistUpdateModal.RmiMembId);
      setTaskStatusId(props.cmWorklistUpdateModal.RmiTaskStatusId);
    } else {
      reset();
    }
  }, [props.isCmWorkListModalVisible]);

  const saveNewTask = () => {
    const formData = {
      RmiCompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      RmiCompletedDate: completedDate,
      RmiCreatedBy: sessionStorage.user_id,
      RmiCreatedDate: moment().utc().toISOString(),
      RmiDueDate: moment(dueDate).utc().toISOString(),
      RmiMembMpiNo: props.eligibilityMemberInfo.RmiMembMpiNo,
      RmiModifiedBy: sessionStorage.user_id,
      RmiModifiedDate: moment().utc().toISOString(),
      RmiNextReviewDate: moment(nextReviewDate).utc().toISOString(),
      RmiTaskAcuityId: taskAcuityId,
      RmiTaskDescription: taskDescription,
      RmiTaskOwner: taskOwner,
      RmiTaskStatusId: taskStatusId,
      RmiUid: uId,
      editInline: true,
    };

    return props.saveTask(formData)
      .then(() => {
        notifier.success("New task saved successfully."); //TODO api doesn't have response message
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message ||"Something went wrong, please contact a system administrator.");
      });
  };

  const updateTask = () => {

    const formData = {
      RmiBirth: props.eligibilityMemberInfo.RmiBirth,
      RmiCompanyId: props.eligibilityMemberInfo.RmiCompanyId,
      RmiCompletedDate: completedDate,
      RmiCreatedBy: sessionStorage.user_id,
      RmiCreatedDate: moment().utc().toISOString(),
      RmiDueDate: moment(dueDate).utc().toISOString(),
      RmiMembId: membId,
      RmiMembMpiNo: props.eligibilityMemberInfo.RmiMembMpiNo,
      RmiModifiedBy: sessionStorage.user_id,
      RmiModifiedDate: moment().utc().toISOString(),
      RmiNextReviewDate: moment(nextReviewDate).utc().toISOString(),
      RmiRevFullName: props.eligibilityMemberInfo.RmiRevFullName,
      RmiTaskAcuity: getEnumKeyByEnumValue(CMWorkListAcuity, taskAcuityId),
      RmiTaskAcuityId: taskAcuityId,
      RmiTaskDescription: taskDescription,
      RmiTaskOwner: taskOwner,
      RmiTaskStatus: getEnumKeyByEnumValue(CMWorkListStatus, taskStatusId),
      RmiTaskStatusId: taskStatusId,
      RmiUid: uId,
    }

    return props.saveTask(formData)
      .then(() => {
        notifier.success("Task saved successfully."); //TODO api doesn't have response message
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message ||"Something went wrong, please contact a system administrator.");
      });
  }

  const reset = () => {
    setUid(0);
    setTaskOwner("");
    setTaskAcuityId(1);
    setTaskStatusId(1);
    setTaskDescription("");
    setDueDate(moment().format('MM/DD/YYYY'));
    setCompletedDate("");
    setNextReviewDate(moment().add(3, 'days').format('MM/DD/YYYY'));
    setMembId("");
    setTaskStatusId(CMWorkListStatus.OPEN);
    form.resetFields();
  }

  const handleOk = () => {
    form.validateFields().then(() => {
      if (props.updateOrNewTask === "updateTask") {
        updateTask();
      } else {
        saveNewTask();
      }
      return props.isCmWorkListModalVisibleHandler();
    });
  };

  const handleCancel = () => {
    reset();
    return props.isCmWorkListModalVisibleHandler();
  };

  const validationRules = async (fieldData: any, value: any) => {
    if ((fieldData.field === 'dueDate' || fieldData.field === 'completedDate' || fieldData.field === 'nextReviewDate')
      && value && (!moment(value).isValid() || moment(value).year() < 1900 || !value.match(/^\d{2}\/\d{2}\/\d{4}$/))) {
      return Promise.reject(new Error('Invalid Date'));
    }

    return Promise.resolve();
  }

  return (
    <>
      <Modal
        visible={props.isCmWorkListModalVisible}
        okText="Save"
        width={1000}
        cancelText="Cancel"
        onCancel={handleCancel}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            type="primary"
            key="save"
            form="modifyAuthForm"
            onClick={handleOk}
          >
            Save
          </Button>
        ]}
      >
        <Title level={4} className="text-center">
          Member Care Task
        </Title>

        <Divider />

        <div className="row mb-3">
          <div className="col-lg-8 col-md-8">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <td>
                    <span><b>Member Name:</b> </span>
                    <span>{props.eligibilityMemberInfo.RmiLastName}, {props.eligibilityMemberInfo.RmiFirstName}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span><b>Gender:</b> </span>
                    <span>{props.eligibilityMemberInfo.RmiSex}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="col-lg-8 col-md-8">
            <table className="test" style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <td>
                    <span><b>Date of Birth:</b> </span>
                    <span>{formatDate(props.eligibilityMemberInfo.RmiBirth, "MM/DD/YYYY")}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span><b>Age:</b> </span>
                    <span>{moment().diff(props.eligibilityMemberInfo.RmiBirth, "years")}</span>
                  </td>
                </tr>
              </tbody>
          </table>
          </div>

          <div className="col-lg-8 col-md-8">
            <table className="test" style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <td>
                    <span><b>PCP:</b> </span>
                    <span>{`${props.primaryCareProvider.RmiLastName}, ${props.primaryCareProvider.RmiFirstName}`}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <Divider />

        <Form
          form={form}
          name="care-task-form"
          onFinish={handleOk}
        >
          <div className="row">
            <div className="col-lg">
              <FloatLabel label="Task Owner"
                name="taskOwner"
                value={taskOwner}
                form={form}
                rules={[
                  () => ({
                    async validator() {
                      if (taskOwner.length === 0) {
                        return Promise.reject(new Error('Required.'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
                star
              >
                <Form.Item>
                  <Input
                    type="text"
                    value={taskOwner}
                    onChange={(e) => setTaskOwner(e.target.value)}
                  />
                </Form.Item>
              </FloatLabel>
            </div>

            <div className="col-lg">
              <FloatLabel label="Acuity" value={taskAcuityId}>
                <Select
                  value={taskAcuityId}
                  placeholder="Select"
                  onChange={(value) => setTaskAcuityId(value)}
                >
                  <option value={CMWorkListAcuity.LOW}>LOW</option>
                  <option value={CMWorkListAcuity.MEDIUM}>MEDIUM</option>
                  <option value={CMWorkListAcuity.HIGH}>HIGH</option>
                </Select>
              </FloatLabel>
            </div>

            <div className="col-lg">
              <FloatLabel label="Status" value={taskStatusId}>
                <Select
                  value={taskStatusId}
                  placeholder="Select"
                  onChange={(value) => setTaskStatusId(value)}
                >
                  <option value={CMWorkListStatus.OPEN}>OPEN</option>
                  <option value={CMWorkListStatus.PENDING}>PENDING</option>
                  <option value={CMWorkListStatus.COMPLETED}>COMPLETED</option>
                </Select>
              </FloatLabel>
            </div>
          </div>

          <div className="row">
            <div className="col-lg">
              <FloatLabel label="Task Description"
                name="taskDescription"
                value={taskDescription}
                form={form}
                rules={[
                  () => ({
                    async validator() {
                      if (taskDescription.length === 0) {
                        return Promise.reject(new Error('Required.'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
                star
              >
                <Form.Item>
                  <TextArea
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                  />
                </Form.Item>
              </FloatLabel>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8 col-md-8">
              <FloatLabel
                name="dueDate"
                label="Due Date (MM/DD/YYYY)"
                value={dueDate}
                form={form}
                rules={[
                  () => ({
                    validator: validationRules
                  }),
                ]}
              >
                <DatePickerLocalised
                  date={dueDate}
                  setDate={setDueDate}
                  formInstance={form}
                  formItemName={'dueDate'}
                />
              </FloatLabel>
            </div>

            <div className="col-lg-8 col-md-8">
              <FloatLabel
                name="completedDate"
                label="Completed Date (MM/DD/YYYY)"
                value={completedDate}
                form={form}
                rules={[
                  () => ({
                    validator: validationRules
                  }),
                ]}
              >
                <DatePickerLocalised
                  date={completedDate}
                  setDate={setCompletedDate}
                  formInstance={form}
                  formItemName={'completedDate'}
                />
              </FloatLabel>
            </div>

            <div className="col-lg-8 col-md-8">
              <FloatLabel
                name="nextReviewDate"
                label="Next Review Date (MM/DD/YYYY)"
                value={nextReviewDate}
                form={form}
                rules={[
                  () => ({
                    validator: validationRules
                  }),
                ]}
              >
                <DatePickerLocalised
                  date={nextReviewDate}
                  setDate={setNextReviewDate}
                  formInstance={form}
                  formItemName={'nextReviewDate'}
                />
              </FloatLabel>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default CaseManagementWorkListModal;
